export default {
  accounts: {
    "1100": "Cash and Cash Equivalents",
    "1110": "Cash on Hand",
    "1120": "Bank Deposits",
    "1130": "Stripe Clearing",
    "1200": "Accounts Receivable",
    "1210": "Rental Receivable",
    "1220": "Utility Receivable",
    "1300": "Prepaid Expenses",
    "1400": "Security Deposits Paid",
    "1500": "Property and Equipment",
    "1510": "Buildings",
    "1520": "Furniture and Fixtures",
    "1530": "Office Equipment",
    "1590": "Accumulated Depreciation",
    "2100": "Accounts Payable",
    "2200": "Accrued Expenses",
    "2300": "Security Deposits Received",
    "2350": "Unapplied Payments / Customer Deposits",
    "2400": "Advance Rental Income",
    "2500": "VAT Payable",
    "2600": "Withholding Tax Payable",
    "3100": "Owner's Equity",
    "3200": "Retained Earnings",
    "4100": "Rental Income",
    "4200": "Common Area Fee Income",
    "4300": "Utility Income",
    "4310": "Water Income",
    "4320": "Electricity Income",
    "4400": "Parking Income",
    "4500": "Late Fee Income",
    "4600": "Other Income",
    "4650": "Reservation Fee Income",
    "4900": "Sales Discounts and Promotions",
    "4950": "Forfeited Deposits & Damage Recovery",
    "5100": "Property Maintenance",
    "5150": "Payment Processing Fees",
    "5200": "Utility Expenses",
    "5210": "Water Expense",
    "5220": "Electricity Expense",
    "5300": "Insurance Expense",
    "5400": "Depreciation Expense",
    "5500": "Salary and Wages",
    "5600": "Administrative Expenses",
    "5700": "Repair and Maintenance",
    "5800": "Supplies Expense",
    "5900": "Other Expenses",
  },

  title: 'Accounting',

  chart_of_accounts: {
    title: 'Chart of Accounts',
    description: 'Manage your general ledger accounts',
    add_account: 'Add Account',
    seed_accounts: 'Seed Standard Accounts',
    seed_confirm: 'This will create standard chart of accounts. Existing system accounts will not be modified. Continue?',
    seed_success: 'Standard accounts seeded successfully',
    delete_confirm: 'Are you sure you want to delete this account?',
    delete_success: 'Account deleted successfully',
    create_success: 'Account created successfully',
    update_success: 'Account updated successfully',
    system_account: 'System Account',
    cannot_delete_system: 'System accounts cannot be deleted',
    empty_title: 'No accounts yet',
    empty_description: 'Seed standard accounts or create custom accounts to get started',
  },

  opening_balance: {
    title: 'Set opening balance',
    balance: 'Balance',
    as_of_date: 'As of date',
    bank_balance: 'Bank balance: {{balance}}',
    description: 'Enter the bank balance held before transactions were recorded in FlowRent.',
    status_error: 'Could not check whether an opening balance has been set. Please retry.',
    invalid_balance: 'Enter a valid balance.',
    date_required: 'Enter the as-of date.',
  },

  errors: {
    opening_balance_already_exists: 'An opening balance has already been posted for this account.',
    opening_balance_missing_gl_account: 'Set up Bank Deposits (1120) and Owner’s Equity (3100) before setting the opening balance.',
    validation_error: 'Check the balance and as-of date, then try again.',
    fields: {
      balance: 'Balance',
      as_of_date: 'As-of date',
    },
    codes: {
      blank: 'is required',
      invalid: 'is invalid',
      invalid_amount: 'must be greater than zero with no more than two decimal places',
      amount_too_large: 'must not exceed 9,999,999,999.99',
      invalid_date: 'must be a valid calendar date',
      not_a_number: 'must be a number',
      greater_than: 'must be greater than zero',
      greater_than_or_equal_to: 'must be zero or greater',
    },
  },

  journal_entries: {
    title: 'Journal Entries',
    description: 'Record and manage accounting journal entries',
    new_entry: 'New Journal Entry',
    post_entry: 'Post Entry',
    void_entry: 'Void Entry',
    post_confirm: 'Post this journal entry? Once posted, lines cannot be edited.',
    void_reason: 'Void Reason',
    void_reason_placeholder: 'Enter reason for voiding this entry...',
    void_confirm: 'Void this journal entry?',
    void_success: 'Journal entry voided',
    post_success: 'Journal entry posted',
    create_success: 'Journal entry created',
    update_success: 'Journal entry updated',
    delete_confirm: 'Delete this draft journal entry?',
    delete_success: 'Journal entry deleted',
    add_line: 'Add Line',
    remove_line: 'Remove',
    balance_ok: 'Balanced',
    balance_off: 'Out of balance',
    debit_total: 'Total Debits',
    credit_total: 'Total Credits',
    difference: 'Difference',
    draft_only_edit: 'Only draft entries can be edited',
    empty_title: 'No journal entries',
    empty_description: 'Create journal entries to record financial transactions',
    // T139-002b — /accounting/journal-entries/:id for a missing id rendered
    // a blank page (`if (!entry) return null`). Matches the invoices/
    // customers/leases detail-page not-found pattern (EmptyState + a button
    // back to the list).
    not_found: 'Journal entry not found',
    back_to_journal_entries: 'Back to Journal Entries',
    reference_types: {
      invoice: 'Invoice',
      payment: 'Payment',
      expense: 'Expense',
      stripe_payout: 'Stripe Payout',
      late_fee_line: 'Late fee line',
    },
  },

  financial_statements: {
    title: 'Financial Statements',
    description: 'View income statement, balance sheet, and cash flow',
    income_statement: 'Income Statement',
    balance_sheet: 'Balance Sheet',
    cash_flow: 'Cash Flow Statement',
    export_csv: 'Export CSV',
    revenue: 'Revenue',
    expenses: 'Expenses',
    net_income: 'Net Income',
    assets: 'Assets',
    liabilities: 'Liabilities',
    equity: 'Equity',
    total_assets: 'Total Assets',
    total_liabilities: 'Total Liabilities',
    retained_earnings: 'Retained Earnings',
    current_year_earnings: 'Current Year Earnings',
    total_equity: 'Total Equity',
    liabilities_and_equity: 'Total Liabilities & Equity',
    operating_activities: 'Operating Activities',
    investing_activities: 'Investing Activities',
    financing_activities: 'Financing Activities',
    net_change: 'Net Change in Cash',
    beginning_cash: 'Beginning Cash',
    ending_cash: 'Ending Cash',
    as_of_date: 'As of Date',
    start_date: 'Start Date',
    end_date: 'End Date',
    total: 'Total',
    subtotal: 'Subtotal',
    empty_title: 'No data available',
    empty_description: 'Select a date range to view financial statements',
  },

  fixed_assets: {
    title: 'Fixed Asset Register',
    description: 'Track and depreciate fixed assets',
    new_asset: 'New Fixed Asset',
    dispose_asset: 'Dispose Asset',
    dispose_confirm: 'Dispose this asset? This action cannot be undone.',
    dispose_date: 'Disposal Date',
    dispose_amount: 'Sale / Disposal Amount',
    dispose_success: 'Asset disposed successfully',
    create_success: 'Fixed asset created successfully',
    update_success: 'Fixed asset updated successfully',
    delete_confirm: 'Delete this fixed asset?',
    delete_success: 'Fixed asset deleted successfully',
    empty_title: 'No fixed assets',
    empty_description: 'Register fixed assets to track depreciation and net book value',
  },

  labels: {
    code: 'Account Code',
    name: 'Account Name',
    name_th: 'Account Name (TH)',
    account_type: 'Account Type',
    normal_balance: 'Normal Balance',
    active: 'Active',
    parent_account: 'Parent Account',
    entry_number: 'Entry Number',
    entry_date: 'Entry Date',
    description: 'Description',
    status: 'Status',
    total_debit: 'Total Debit',
    total_credit: 'Total Credit',
    reference: 'Reference',
    property: 'Property',
    account: 'Account',
    debit: 'Debit',
    credit: 'Credit',
    acquisition_date: 'Acquisition Date',
    acquisition_cost: 'Acquisition Cost',
    salvage_value: 'Salvage Value',
    useful_life: 'Useful Life (months)',
    depreciation_method: 'Depreciation Method',
    accumulated_depreciation: 'Accumulated Depreciation',
    net_book_value: 'Net Book Value',
    gl_account: 'GL Account',
    depreciation_account: 'Depreciation Expense Account',
    disposed_at: 'Disposed At',
    useful_life_months_count_one: '{{count}} month',
    useful_life_months_count_other: '{{count}} months',
  },

  placeholders: {
    code: 'e.g., 1000',
    name: 'e.g., Cash and Cash Equivalents',
    name_th: 'เช่น เงินสดและรายการเทียบเท่าเงินสด',
    description: 'Enter description',
    search_account: 'Search accounts...',
    void_reason: 'Enter reason for voiding...',
    asset_name: 'e.g., Air Conditioner Unit',
  },

  account_types: {
    asset: 'Asset',
    liability: 'Liability',
    equity: 'Equity',
    revenue: 'Revenue',
    expense: 'Expense',
  },

  normal_balances: {
    debit: 'Debit',
    credit: 'Credit',
  },

  statuses: {
    draft: 'Draft',
    posted: 'Posted',
    voided: 'Voided',
  },

  depreciation_methods: {
    straight_line: 'Straight Line',
    declining_balance: 'Declining Balance',
  },

  asset_statuses: {
    active: 'Active',
    fully_depreciated: 'Fully Depreciated',
    disposed: 'Disposed',
  },

  filters: {
    all_types: 'All Types',
    all_statuses: 'All Statuses',
    all_methods: 'All Methods',
    all_properties: 'All Properties',
    property: 'Property',
    date_from: 'From',
    date_to: 'To',
    status: 'Status',
    none: '— None —',
    none_root_account: '— None (root account) —',
  },

  messages: {
    error_loading: 'Failed to load data',
    error_description: 'Please try again later',
    balanced: 'Entry is balanced',
    not_balanced: 'Entry is not balanced. Debits must equal credits',
  },

  setup_guide: {
    title: 'Set Up GL Accounting',
    description: 'Configure your chart of accounts to start recording financial transactions and generating financial statements.',
    step_1_title: 'Seed Standard Accounts',
    step_1_description: 'Create standard Thai chart of accounts as a starting point.',
    step_2_title: 'Create Journal Entries',
    step_2_description: 'Record financial transactions with double-entry bookkeeping.',
    step_3_title: 'Generate Statements',
    step_3_description: 'View income statement, balance sheet, and cash flow reports.',
  },
}
